import React from 'react';

import get from 'lodash/get';

import { SidePage, Section } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import {
  InfluencerCampaignFormDuplicate,
  InfluencerCampaignFormEdit,
} from 'scenes/Influencers/InfluencersCampaigns/components/InfluencerCampaignForm';
import InfluencerChannelDetails from 'scenes/Influencers/InfluencersChannels/components/InfluencerChannelDetails';
import InfluencerDetails from 'scenes/Influencers/InfluencersChannels/components/Influencers/components/InfluencerDetails/InfluencerDetails';
import MasterCampaignDetails from 'scenes/Influencers/MasterCampaigns/components/MasterCampaignDetails';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';
import PromoCodeDetails from 'scenes/PromoCodes/components/PromoCodeDetails';

import { useLoadInfluencerCampaignDetails, useIsUpdating } from '../../data';
import InfluencersCampaignActions from '../Actions';
import GGCampaigns from './GGCampaigns';
import General from './General';
import GgLinkExpirationDate from './GgLinkExpirationDate';
import Metadata from './Metadata';
import Reward from './Reward';
import DynamicReward from './DynamicReward';
import { useScreenGo } from '@gogame/screens';

const InfluencerCampaignDetails = ({
  id,
  onClose,
  onChange,
  onExited,
  open,
}) => {
  const { data, isLoading, isRefetching } =
    useLoadInfluencerCampaignDetails(id);

  const isUpdating = useIsUpdating();

  const go = useScreenGo();
  const [childComponent, openChildComponent] = useChildComponent();

  const handleEdit = () => {
    openChildComponent(InfluencerCampaignFormEdit, {
      id: data.id,
    });
  };

  const handleOpenMasterCampaign = (_e, { id }) => {
    openChildComponent(MasterCampaignDetails, {
      id,
    });
  };

  const handleOpenAffiliate = (_e, { id }) => {
    openChildComponent(AffiliateDetails, {
      id,
    });
  };

  const handleOpenInfluencer = (_e, { id }) => {
    openChildComponent(InfluencerDetails, {
      id,
    });
  };

  const handleOpenInfluencerChannel = (_e, { id }) => {
    openChildComponent(InfluencerChannelDetails, {
      id,
    });
  };

  const handleOpenPromoCode = (_e, { id: promoCode }) => {
    openChildComponent(PromoCodeDetails, {
      id: promoCode,
    });
  };

  const handleDuplicate = ({ id }) => {
    openChildComponent(InfluencerCampaignFormDuplicate, {
      id,
      onSubmit: (campaign) => {
        go(`../${campaign.id}`);
      },
    });
  };

  const showAdditionalReward = data?.additionalRewards?.length > 0;
  const showDynamicAdditionalReward =
    data?.dynamicAdditionalRewards?.length > 0;

  const showReward = !!data?.rewardPackage?.id || showAdditionalReward;
  const showDynamicReward =
    !!data?.dynamicReward?.length || showDynamicAdditionalReward;

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading || isUpdating}
      fetching={isRefetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={'Influencer Campaign'}
        subTitle={get(data, 'name')}
        entityId={id}
      />
      <SidePage.NewBody wrapper={true} dense={true}>
        <SidePage.NewContent>
          <Section title={'General'}>
            <General
              data={data}
              onOpenMasterCampaign={handleOpenMasterCampaign}
              onOpenPromoCode={handleOpenPromoCode}
              onOpenAffiliate={handleOpenAffiliate}
              onOpenInfluencer={handleOpenInfluencer}
              onOpenInfluencerChannel={handleOpenInfluencerChannel}
            />
          </Section>

          {showReward ? (
            <Section title={'Reward'} isCollapsable={true}>
              <Reward data={data} showAdditionalReward={showAdditionalReward} />
            </Section>
          ) : null}

          {showDynamicReward ? (
            <Section title={'Dynamic reward'} isCollapsable={true}>
              <DynamicReward
                data={data}
                showAdditionalReward={showDynamicAdditionalReward}
              />
            </Section>
          ) : null}
          {data?.ggLinksExpirationDate ? (
            <Section title={'GG Link(s) Expiration Date'} isCollapsable={true}>
              <GgLinkExpirationDate data={data} />
            </Section>
          ) : null}
          {data?.goGameCampaigns ? (
            <GGCampaigns
              data={data.goGameCampaigns}
              hybridLink={data.hybridLink}
              onChange={onChange}
            />
          ) : null}
        </SidePage.NewContent>
        <SidePage.Sidebar
          actions={
            <InfluencersCampaignActions
              data={data}
              actions={data?.actions}
              onEdit={handleEdit}
              onDelete={onClose}
              onDuplicate={handleDuplicate}
            />
          }
        >
          <Metadata data={data} />
        </SidePage.Sidebar>
      </SidePage.NewBody>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};

export default InfluencerCampaignDetails;
